import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { PhoneCall, CheckCircle2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export const DemoModal = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ nombre: '', empresa: '', telefono: '', email: '', num_empleados: '', mensaje: '' });
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const enviar = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try {
      await axios.post(`${API}/api/public/demo-request`, form);
      setEnviado(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se ha podido enviar la solicitud. Inténtalo de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const cerrar = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => setEnviado(false), 300);
  };

  return (
    <Dialog open={open} onOpenChange={cerrar}>
      <DialogContent className="sm:max-w-md bg-bone border-forest-900/10 rounded-3xl p-8" data-testid="demo-modal">
        {enviado ? (
          <div className="py-10 text-center" data-testid="demo-success">
            <CheckCircle2 className="w-14 h-14 text-forest-700 mx-auto mb-5" />
            <h3 className="font-display text-2xl font-bold text-forest-900 mb-2">¡Solicitud recibida!</h3>
            <p className="font-body-landing text-forest-700">Te llamaremos muy pronto para enseñarte GestionaEmpleados en directo. Gracias por tu interés.</p>
            <Button onClick={() => cerrar(false)} className="mt-6 rounded-full bg-forest-900 hover:bg-forest-800 text-bone px-8" data-testid="demo-close-btn">Cerrar</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="w-11 h-11 rounded-2xl bg-terracotta/10 flex items-center justify-center mb-2">
                <PhoneCall className="w-5 h-5 text-terracotta" />
              </div>
              <DialogTitle className="font-display text-2xl font-bold text-forest-900">Solicita una demo personalizada</DialogTitle>
              <DialogDescription className="font-body-landing text-forest-700">
                Déjanos tus datos y te llamamos para mostrarte cómo funciona, sin compromiso.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={enviar} className="space-y-4 mt-2 font-body-landing">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="demo-nombre" className="text-forest-900">Nombre *</Label>
                  <Input id="demo-nombre" required value={form.nombre} onChange={set('nombre')} placeholder="Tu nombre" className="rounded-xl bg-white border-forest-900/15" data-testid="demo-nombre-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="demo-empresa" className="text-forest-900">Empresa *</Label>
                  <Input id="demo-empresa" required value={form.empresa} onChange={set('empresa')} placeholder="Nombre de tu empresa" className="rounded-xl bg-white border-forest-900/15" data-testid="demo-empresa-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="demo-telefono" className="text-forest-900">Teléfono *</Label>
                  <Input id="demo-telefono" required type="tel" value={form.telefono} onChange={set('telefono')} placeholder="600 000 000" className="rounded-xl bg-white border-forest-900/15" data-testid="demo-telefono-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="demo-email" className="text-forest-900">Email *</Label>
                  <Input id="demo-email" required type="email" value={form.email} onChange={set('email')} placeholder="tu@empresa.com" className="rounded-xl bg-white border-forest-900/15" data-testid="demo-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-forest-900">Nº de empleados</Label>
                <Select value={form.num_empleados} onValueChange={(v) => setForm({ ...form, num_empleados: v })}>
                  <SelectTrigger className="rounded-xl bg-white border-forest-900/15" data-testid="demo-empleados-select">
                    <SelectValue placeholder="Selecciona un rango" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1 - 10</SelectItem>
                    <SelectItem value="11-25">11 - 25</SelectItem>
                    <SelectItem value="26-50">26 - 50</SelectItem>
                    <SelectItem value="51-100">51 - 100</SelectItem>
                    <SelectItem value="+100">Más de 100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" disabled={enviando} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover text-white font-bold h-12 text-base" data-testid="demo-submit-btn">
                {enviando ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Quiero que me llaméis'}
              </Button>
              <p className="text-xs text-forest-700/70 text-center">Sin compromiso. Solo usaremos tus datos para contactarte sobre la demo.</p>
            </form>
            <div className="mt-5 pt-5 border-t border-forest-900/10 space-y-2 text-center">
              <p className="text-xs text-forest-700/60 font-semibold">¿Prefieres llamar tú? Estamos disponibles L-V, 9-18h</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
                <a href="tel:+34680292427" className="inline-flex items-center gap-1.5 text-forest-900 font-bold hover:text-terracotta transition-colors" data-testid="demo-modal-phone">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta" /> 680 292 427
                </a>
                <a href="mailto:comercial@gestionaempleados.com" className="inline-flex items-center gap-1.5 text-forest-900 font-bold hover:text-terracotta transition-colors break-all" data-testid="demo-modal-email">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-terracotta" /> comercial@gestionaempleados.com
                </a>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
